import fs from 'node:fs/promises';

import { writeBufferSnapshot } from '../upload/files.ts';
import type { LocalThreadFile } from '../upload/harnesses/types.ts';
import { redactTranscript } from './redact.ts';
import { type Finding, type ScanResult, scanTranscript } from './scan.ts';

// A LocalThreadFile that has passed the scrub gate. The brand is phantom and
// can only be applied here (`brand()`), so `createUploadSession`, which requires
// this type, is uncallable on un-scrubbed input. (TAN-4341)
declare const scrubbedBrand: unique symbol;
export type ScrubbedThreadFile = LocalThreadFile & { readonly [scrubbedBrand]: true };

export type ScrubOutcome =
  | { status: 'clean'; file: ScrubbedThreadFile }
  | { status: 'redacted'; file: ScrubbedThreadFile; redactedRuleIds: string[] }
  | {
      status: 'quarantined';
      reason: 'risky' | 'unparsable' | 'scan_error' | 'unredactable';
      findings: Finding[];
    };

export type ScrubThreadFileOptions = {
  /** Injected in tests; defaults to the real scanner. */
  scan?: (jsonl: string) => ScanResult;
};

function brand(file: LocalThreadFile): ScrubbedThreadFile {
  return file as ScrubbedThreadFile;
}

/**
 * Scan one snapshot and decide its fate before upload:
 *  - no findings            → clean (uploaded as-is)
 *  - all redact-disposition → redacted (rewritten + md5 recomputed, uploaded)
 *  - anything else          → quarantined (held locally, never uploaded)
 *
 * Fail-closed: an unparsable line, a scan/read error, or a redaction that can't
 * be cleanly applied all quarantine rather than upload.
 */
export async function scrubThreadFile(
  localFile: LocalThreadFile,
  options: ScrubThreadFileOptions = {},
): Promise<ScrubOutcome> {
  const scan = options.scan ?? scanTranscript;

  // Fail-closed: ANY error reading, scanning, redacting, or re-snapshotting
  // quarantines rather than uploads. The whole body is guarded so an
  // unexpected throw (disk full, malformed input) can never fall through to
  // an upload.
  try {
    const text = await fs.readFile(localFile.tempPath, 'utf8');
    const result: ScanResult = scan(text);

    // Don't upload a file we couldn't fully scan.
    if (result.unparsableLines.length > 0) {
      return { status: 'quarantined', reason: 'unparsable', findings: result.findings };
    }
    if (result.findings.length === 0) {
      return { status: 'clean', file: brand(localFile) };
    }
    // Any quarantine-disposition finding holds the whole thread.
    if (result.findings.some((f) => f.disposition === 'quarantine')) {
      return { status: 'quarantined', reason: 'risky', findings: result.findings };
    }

    // All findings are redact-disposition: rewrite in place and re-snapshot.
    const redacted = redactTranscript(text, result.findings);
    if (!redacted.ok) {
      return { status: 'quarantined', reason: 'unredactable', findings: result.findings };
    }
    const snapshot = await writeBufferSnapshot(localFile.filename, redacted.text);
    const file = brand({ ...localFile, tempPath: snapshot.tempPath, md5: snapshot.md5 });
    const redactedRuleIds = [...new Set(result.findings.map((f) => f.ruleId))].sort();
    return { status: 'redacted', file, redactedRuleIds };
  } catch {
    return { status: 'quarantined', reason: 'scan_error', findings: [] };
  }
}
